import { timeTrackCategoryLabels } from "@shared/timetrack";

export interface ReadableHistoryEvent {
  action: string; actorId: number | null; actorName?: string | null;
  before: unknown; after: unknown; createdAt: string;
}

export function historyTimestamp(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Date not recorded" : date.toLocaleString(undefined, {
    year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

/** History durations keep the evidence precise, while dates never show seconds. */
export function historyDuration(value: number) {
  const total = Math.max(0, Math.floor(value));
  const remainder = total % 60;
  return `${Math.floor(total / 3600)}h ${Math.floor(total / 60) % 60}m${remainder ? ` ${remainder}s` : ""}`;
}

function snapshot(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
}
function text(value: unknown) { return typeof value === "string" && value.trim() ? value : undefined; }
function category(value: unknown) {
  return typeof value === "string" && Object.hasOwn(timeTrackCategoryLabels, value)
    ? timeTrackCategoryLabels[value as keyof typeof timeTrackCategoryLabels] : undefined;
}
const actionLabels: Record<string, string> = {
  started: "Timer started", created: "Time added", pause: "Timer paused", auto_paused: "Timer paused when switching work",
  resume: "Timer continued", resumed: "Timer continued", stop: "Session finished", snooze: "Reminder postponed",
  review: "Recorded time confirmed", correct: "Time updated", delete: "Entry deleted", restore: "Entry restored",
  split: "Time split across work items", split_created: "Time allocated from a split session", legacy_import: "Earlier time brought into TimeTrack",
};

/** Allowlist the actual Stored/legacy snapshots; never stringify arbitrary audit data. */
export function readableHistory(event: ReadableHistoryEvent, currentUserId?: number) {
  const before = snapshot(event.before);
  const after = snapshot(event.after);
  const values: string[] = [];
  const actor = text(event.actorName) || (currentUserId != null && event.actorId === currentUserId ? "You" : "Team member");
  const restricted = before.restricted === true || after.restricted === true;
  const beforeLocation = [text(before.projectName), text(before.targetLabel)].filter(Boolean).join(" / ");
  const afterLocation = [text(after.projectName), text(after.targetLabel)].filter(Boolean).join(" / ");
  const change = (label: string, oldValue: string | undefined, newValue: string | undefined) => {
    if (oldValue === newValue) return;
    if (newValue !== undefined) values.push(oldValue !== undefined ? `${label}: ${oldValue} → ${newValue}` : `${label}: ${newValue}`);
  };
  if (restricted) {
    values.push("Some earlier or later details belong to a work item you cannot access.");
    return { title: actionLabels[event.action] || "Entry updated", actor, timestamp: historyTimestamp(event.createdAt), values };
  }
  if (event.action === "legacy_import") {
    // The old SQL row is evidence of entered time, not a stopwatch interval.
    if (typeof before.duration_minutes === "number") values.push(`Earlier recorded duration: ${historyDuration(before.duration_minutes * 60)}`);
    if (afterLocation) values.push(`Work item: ${afterLocation}`);
    if (typeof after.durationSeconds === "number") values.push(`Duration: ${historyDuration(after.durationSeconds)}`);
    if (text(after.workDate)) values.push(`Work date: ${after.workDate}`);
    if (category(after.category)) values.push(`Kind of work: ${category(after.category)}`);
    if (text(after.note)) values.push(`Note: ${after.note}`);
    values.push("Original creator not recorded. Earlier dates are not timer working periods.");
  } else {
    change("Work item", beforeLocation || undefined, afterLocation || undefined);
    change("Duration", typeof before.durationSeconds === "number" ? historyDuration(before.durationSeconds) : undefined,
      typeof after.durationSeconds === "number" ? historyDuration(after.durationSeconds) : undefined);
    change("Work date", text(before.workDate), text(after.workDate));
    change("Kind of work", category(before.category), category(after.category));
    if (before.note !== after.note) change("Note", text(before.note) || (event.before ? "No note" : undefined), text(after.note) || "No note");
    const states: Record<string, string> = { running: "Running", paused: "Paused", finished: "Finished" };
    change("Session", states[String(before.state)], states[String(after.state)]);
    if (before.deletedAt !== after.deletedAt && (before.deletedAt || after.deletedAt)) values.push(after.deletedAt ? "Removed from totals; kept in history." : "Included in totals again.");
    if (event.action === "split" && Array.isArray(after.splitInto)) values.push(`Divided into ${after.splitInto.length} entries. Original working periods are kept here.`);
    if (event.action === "split_created") values.push("Duration allocated from the original session; no separate working periods were recorded.");
    if (event.action === "snooze" && text(after.reminderAt)) values.push(`Next reminder: ${historyTimestamp(after.reminderAt as string)}`);
    if (event.action === "review" && before.needsReview !== after.needsReview) values.push("The artist confirmed the recorded working periods.");
  }
  return { title: actionLabels[event.action] || "Entry updated", actor, timestamp: historyTimestamp(event.createdAt), values };
}
